import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Navbar, Nav, Container, Badge, Dropdown } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import { toast } from 'react-toastify';
import api from '../config/api';
import FullPageLoader from './FullPageLoader';
import './Layout.css';
import logo from '../assets/freakers_logo.png';
import brandLogo from '../assets/brand_logo.png';
const Layout = () => {
  const { logout, isAuthenticated } = useAuth();
  const { pageLoading } = usePageLoader();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchOnScroll, setShowSearchOnScroll] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showNavbar, setShowNavbar] = useState(true);
  const [navbarExpanded, setNavbarExpanded] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCartCount();
      fetchWishlistCount();
    }
    fetchCategories();
  }, [isAuthenticated]);

  // Listen for cart/wishlist updates from other components
  useEffect(() => {
    const handleCartUpdate = () => {
      if (isAuthenticated) {
        fetchCartCount();
      }
    };

    const handleWishlistUpdate = () => {
      if (isAuthenticated) {
        fetchWishlistCount();
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, [isAuthenticated]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Sync search query with URL when on products page
  useEffect(() => {
    if (location.pathname === '/products') {
      const searchParam = searchParams.get('search') || '';
      setSearchQuery(searchParam);
    } else {
      setSearchQuery('');
    }
  }, [location.pathname, searchParams]);

  // Handle scroll to show/hide navbar and search bar on mobile
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobile = window.innerWidth < 992;
      
      if (isMobile) {
        // On mobile: show navbar when scrolling up, hide when scrolling down
        if (currentScrollY < lastScrollY && currentScrollY > 50) {
          // Scrolling up - show navbar
          setShowNavbar(true);
          setShowSearchOnScroll(true);
        } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scrolling down - hide navbar
          setShowNavbar(false);
          setShowSearchOnScroll(false);
        } else if (currentScrollY < 50) {
          // Near top - always show
          setShowNavbar(true);
          setShowSearchOnScroll(false);
        }
      } else {
        // On desktop: always show (fixed navbar)
        setShowNavbar(true);
        setShowSearchOnScroll(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const fetchCartCount = async () => {
    try {
      const response = await api.get('/cart');
      const count = response.cart?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      setCartCount(count);
    } catch (error) {
      // Ignore errors if not authenticated
    }
  };

  const fetchWishlistCount = async () => {
    try {
      const response = await api.get('/wishlist');
      setWishlistCount(response.wishlist?.length || 0);
    } catch (error) {
      // Ignore errors if not authenticated
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories?is_active=true');
      // Get top-level categories (parent_id === null)
      const topCategories = (response.categories || []).filter(
        (cat) => cat.parent_id === null || cat.parent_id === undefined
      );
      setCategories(topCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      // If search is empty, navigate to products without search param
      navigate('/products');
    }
    // Close navbar on mobile after search
    if (window.innerWidth < 992) {
      setNavbarExpanded(false);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    navigate('/products');
  };

  const handleNavLinkClick = () => {
    // Close navbar when a navigation link is clicked (mobile)
    if (window.innerWidth < 992) {
      setNavbarExpanded(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 layout-wrapper">
      {pageLoading && <FullPageLoader />}
      {/* Gold top strip */}
      <div className="navbar-top-strip"></div>
      
      <Navbar
        expand="lg" 
        variant="dark" 
        className={`custom-navbar ${showNavbar ? 'navbar-visible' : 'navbar-hidden'}`} 
        expanded={navbarExpanded}
        onToggle={setNavbarExpanded}
        collapseOnSelect
      >
        <Container fluid className="px-4 navbar-container">
          {/* Header Row - Always Visible */}
          <div className="navbar-header-row">
            {/* Logo Section */}
            <Navbar.Brand as={Link} to="/" className="navbar-brand-logo">
              <img src={logo} alt="Freakers Logo" className="navbar-logo" />
            </Navbar.Brand>

            {/* Right Icons - Visible on Mobile Only */}
            <Nav className="navbar-nav-icons-header">
              {isAuthenticated ? (
                <>
                  <Dropdown>
                    <Dropdown.Toggle variant="link" className="nav-icon-link text-decoration-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item as={Link} to="/profile">
                        My Profile
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/orders">
                        My Orders
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/returns">
                        My Returns
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Nav.Link as={Link} to="/wishlist" className="nav-icon-link position-relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="23"
                      height="21"
                      viewBox="0 0 23 21"
                      fill="none"
                    >
                      <path
                        d="M20.2913 2.61183C19.7805 2.10083 19.1741 1.69547 18.5066 1.41891C17.8392 1.14235 17.1238 1 16.4013 1C15.6788 1 14.9634 1.14235 14.2959 1.41891C13.6285 1.69547 13.022 2.10083 12.5113 2.61183L11.4513 3.67183L10.3913 2.61183C9.3596 1.58013 7.96032 1.00053 6.50129 1.00053C5.04226 1.00053 3.64298 1.58013 2.61129 2.61183C1.5796 3.64352 1 5.04279 1 6.50183C1 7.96086 1.5796 9.36013 2.61129 10.3918L3.67129 11.4518L11.4513 19.2318L19.2313 11.4518L20.2913 10.3918C20.8023 9.88107 21.2076 9.27464 21.4842 8.60718C21.7608 7.93972 21.9031 7.22431 21.9031 6.50183C21.9031 5.77934 21.7608 5.06393 21.4842 4.39647C21.2076 3.72901 20.8023 3.12258 20.2913 2.61183V2.61183Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {wishlistCount > 0 && (
                      <Badge
                        bg="#B89354"
                        className="position-absolute top-1.5 right-[-6px] translate-middle rounded-circle icon-badge"
                        style={{ fontSize: '0.65rem', minWidth: '18px', height: '18px', padding: '2px 5px' }}
                      >
                        {wishlistCount}
                      </Badge>
                    )}
                  </Nav.Link>
                  <Nav.Link as={Link} to="/cart" className="nav-icon-link position-relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="23"
                      viewBox="0 0 21 23"
                      fill="none"
                    >
                      <path
                        d="M14.5195 10.013V5.50651C14.5195 4.31131 14.0447 3.16506 13.1996 2.31993C12.3545 1.47479 11.2082 1 10.013 1C8.81782 1 7.67157 1.47479 6.82644 2.31993C5.9813 3.16506 5.50651 4.31131 5.50651 5.50651V10.013M2.12663 7.75977H17.8994L19.026 21.2793H1L2.12663 7.75977Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {cartCount > 0 && (
                      <Badge
                        bg="#B89354"
                        className="position-absolute top-1.5 right-[-6px] translate-middle rounded-circle icon-badge"
                        style={{ fontSize: '0.65rem', minWidth: '18px', height: '18px', padding: '2px 5px' }}
                      >
                        {cartCount}
                      </Badge>
                    )}
                  </Nav.Link>
                </>
              ) : (
                <>
                  <Nav.Link as={Link} to="/login" className="nav-icon-link">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Nav.Link>
                </>
              )}
            </Nav>

            {/* Toggle Button */}
            <Navbar.Toggle aria-controls="basic-navbar-nav" className="navbar-toggle" />
          </div>

          {/* Collapsible Menu - Below Header */}
          <Navbar.Collapse 
            id="basic-navbar-nav" 
            className={`navbar-collapse-custom ${showSearchOnScroll && !navbarExpanded ? 'show-search' : ''}`}
          >
            {/* Search Bar - First on Mobile */}
            <form className={`navbar-search-form ${showSearchOnScroll ? 'always-visible' : ''}`} onSubmit={handleSearch}>
              <div className="navbar-search-input-wrapper">
                <input
                  type="text"
                  className="navbar-search-input"
                  placeholder="What are you looking for?"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="navbar-search-clear-btn"
                    onClick={handleSearchClear}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <button type="submit" className="navbar-search-button">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 17.5L13.875 13.875"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </form>

            {/* Navigation Links */}
            <Nav className="navbar-nav-categories">
              <Nav.Link as={Link} to="/products?filter=new" className="nav-link-category" onClick={handleNavLinkClick}>
                NEW IN
                <span className="nav-link-underline"></span>
              </Nav.Link>
              
              <Dropdown className="nav-dropdown">
                <Dropdown.Toggle as={Nav.Link} className="nav-link-category">
                  THE ESSENTIALS
                  <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="nav-link-underline"></span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {categories.slice(0, 5).map((category) => (
                    <Dropdown.Item key={category.id} as={Link} to={`/products?category=${category.id}`} onClick={handleNavLinkClick}>
                      {category.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              
              <Nav.Link as={Link} to="/products?filter=bestsellers" className="nav-link-category" onClick={handleNavLinkClick}>
                BESTSELLERS
                <span className="nav-link-underline"></span>
              </Nav.Link>

              <Nav.Link as={Link} to="/products?filter=combo" className="nav-link-category" onClick={handleNavLinkClick}>
                COMBOS
                <span className="nav-link-underline"></span>
              </Nav.Link>
              
              <Nav.Link as={Link} to="/products?filter=sale" className="nav-link-category nav-link-sale" onClick={handleNavLinkClick}>
                <span className="nav-link-sale-text">SALE</span>
                <span className="nav-link-sale-sparkles" aria-hidden="true">
                  <span className="sparkle sparkle-1"></span>
                  <span className="sparkle sparkle-2"></span>
                  <span className="sparkle sparkle-3"></span>
                </span>
                <span className="nav-link-underline"></span>
              </Nav.Link>
            </Nav>

            {/* Right Icons - Visible on Desktop Only */}
            <Nav className="navbar-nav-icons-desktop">
              {isAuthenticated ? (
                <>
                  <Dropdown>
                    <Dropdown.Toggle variant="link" className="nav-icon-link text-decoration-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item as={Link} to="/profile">
                        My Profile
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/orders">
                        My Orders
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to="/returns">
                        My Returns
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Nav.Link as={Link} to="/wishlist" className="nav-icon-link position-relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="23"
                      height="21"
                      viewBox="0 0 23 21"
                      fill="none"
                    >
                      <path
                        d="M20.2913 2.61183C19.7805 2.10083 19.1741 1.69547 18.5066 1.41891C17.8392 1.14235 17.1238 1 16.4013 1C15.6788 1 14.9634 1.14235 14.2959 1.41891C13.6285 1.69547 13.022 2.10083 12.5113 2.61183L11.4513 3.67183L10.3913 2.61183C9.3596 1.58013 7.96032 1.00053 6.50129 1.00053C5.04226 1.00053 3.64298 1.58013 2.61129 2.61183C1.5796 3.64352 1 5.04279 1 6.50183C1 7.96086 1.5796 9.36013 2.61129 10.3918L3.67129 11.4518L11.4513 19.2318L19.2313 11.4518L20.2913 10.3918C20.8023 9.88107 21.2076 9.27464 21.4842 8.60718C21.7608 7.93972 21.9031 7.22431 21.9031 6.50183C21.9031 5.77934 21.7608 5.06393 21.4842 4.39647C21.2076 3.72901 20.8023 3.12258 20.2913 2.61183V2.61183Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {wishlistCount > 0 && (
                      <Badge
                        bg="#B89354"
                        className="position-absolute top-1.5 right-[-6px] translate-middle rounded-circle icon-badge"
                        style={{ fontSize: '0.65rem', minWidth: '18px', height: '18px', padding: '2px 5px' }}
                      >
                        {wishlistCount}
                      </Badge>
                    )}
                  </Nav.Link>
                  <Nav.Link as={Link} to="/cart" className="nav-icon-link position-relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="21"
                      height="23"
                      viewBox="0 0 21 23"
                      fill="none"
                    >
                      <path
                        d="M14.5195 10.013V5.50651C14.5195 4.31131 14.0447 3.16506 13.1996 2.31993C12.3545 1.47479 11.2082 1 10.013 1C8.81782 1 7.67157 1.47479 6.82644 2.31993C5.9813 3.16506 5.50651 4.31131 5.50651 5.50651V10.013M2.12663 7.75977H17.8994L19.026 21.2793H1L2.12663 7.75977Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {cartCount > 0 && (
                      <Badge
                        bg="#B89354"
                        className="position-absolute top-1.5 right-[-6px] translate-middle rounded-circle icon-badge"
                        style={{ fontSize: '0.65rem', minWidth: '18px', height: '18px', padding: '2px 5px' }}
                      >
                        {cartCount}
                      </Badge>
                    )}
                  </Nav.Link>
                </>
              ) : (
                <>
                  <Nav.Link as={Link} to="/login" className="nav-icon-link">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Nav.Link>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main className="flex-grow-1">
        <Outlet />
      </main>

      <footer className="custom-footer">
        <Container className="footer-content">
          <div className="row g-4 footer-row">
            {/* Brand Section */}
            <div className="col-md-3 col-sm-12 sm:w-100">
              <div className="footer-brand">
                <div className="footer-logo">
                <img src={brandLogo} alt="Freakers Brand" className="ooter-logo-icon" />
                </div>
                
                <h5 className="footer-social-title">FOLLOW US ON</h5>
                <div className="footer-social-icons">
                  <a
                    href="https://www.instagram.com/freakers.__"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-link"
                    aria-label="Instagram"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                  </a>
                  {/* <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-link"
                    aria-label="Facebook"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                    </svg>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon-link"
                    aria-label="Twitter"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                    </svg>
                  </a> */}
                </div>
                <p className="footer-copyright-text">&copy;2026 Freakers. All Rights Reserved.</p>
              </div>
            </div>

            {/* Shop Section */}
            <div className="col-md-3">
              <h5 className="footer-title">SHOP</h5>
              <ul className="footer-links">
                <li>
                  <Link to="/products">All Products</Link>
                </li>
                <li>
                  <Link to="/products?filter=new">New Arrivals</Link>
                </li>
                <li>
                  <Link to="/products?filter=bestsellers">Bestsellers</Link>
                </li>
                <li>
                  <Link to="/products?filter=combo">Combos</Link>
                </li>
                <li>
                  <Link to="/products?filter=sale">Sale</Link>
                </li>
              </ul>
            </div>

            {/* Company Section */}
            <div className="col-md-3">
              <h5 className="footer-title">COMPANY</h5>
              <ul className="footer-links">
                <li>
                  <Link to="/about" className="footer-link">About Us</Link>
                </li>
                <li>
                  <Link to="/contact">Contact us</Link>
                </li>
               
              </ul>
            </div>

            {/* Support Section */}
            <div className="col-md-3">
              <h5 className="footer-title">SUPPORT</h5>
              <ul className="footer-links">

                <li>
                  <Link to="/shipping">Shipping</Link>
                </li>
                <li>
                  <Link to="/returns-policy">Returns</Link>
                </li>
                <li>
                  <Link to="/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/terms">Terms Of Service</Link>
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </footer>
      
      {/* WhatsApp Sticky Icon */}
      <div 
        className="whatsapp-sticky-icon"
        onClick={() => {
          const phoneNumber = '9444363482';
          const defaultMessage = encodeURIComponent('Hello! I would like to know more about your products.');
          const whatsappUrl = `https://wa.me/${phoneNumber}?text=${defaultMessage}`;
          window.open(whatsappUrl, '_blank');
        }}
        title="Chat with us on WhatsApp"
      >
        <svg 
          width="28" 
          height="28" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" 
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  );
};

export default Layout;
